import type { ChatSummary } from '@spot/shared';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { eventChatPath } from '../app/paths';
import { ScreenMessage } from '../components/ui/screen-message';
import { useMyChats } from '../features/chat/use-my-chats';
import { useSession } from '../features/auth/session-gate';
import { listTime } from '../features/chat/chat-grouping';
import { CoverArt } from '../features/events/cover-art';
import { formatDate } from '../features/events/format';
import { DEFAULT_CITY } from '../features/map/city';
import { useMessageText } from '../features/chat/message-bubble';
import { usePersonName } from '../features/profile/person-link';
import { popIn, pressable } from '../components/ui/pressable';
import { ScreenSkeleton } from '../components/ui/skeleton';

/** S10 Chats (T-306d): one row per event chat; tap opens the event on its Chat tab. */
export function ChatsScreen() {
	const { t } = useTranslation();
	const chats = useMyChats();

	if (chats.isPending) return <ScreenSkeleton kind="list" />;
	if (chats.isError) {
		return (
			<ScreenMessage
				title={t('chats.title')}
				body={t('chats.error')}
				action={{ label: t('errors.retry'), onClick: () => void chats.refetch() }}
			/>
		);
	}
	if (chats.data.items.length === 0) {
		return <ScreenMessage emoji="💬" title={t('chats.title')} body={t('chats.empty')} />;
	}
	return (
		<div className="flex flex-col gap-3 px-4 py-4">
			<h1 className="text-h2 text-text font-bold">{t('chats.title')}</h1>
			<ul className="flex flex-col gap-2">
				{chats.data.items.map((chat, index) => (
					<ChatRow key={chat.eventId} chat={chat} index={index} />
				))}
			</ul>
		</div>
	);
}

const pill = 'text-caption rounded-full px-2 py-0.5';

function ChatRow({ chat, index }: { chat: ChatSummary; index: number }) {
	const { t } = useTranslation();
	const { me } = useSession();
	const nameOf = usePersonName();
	const messageText = useMessageText();
	const tz = DEFAULT_CITY.timezone;
	const last = chat.lastMessage;
	const unread = chat.unread > 0;
	const preview =
		last === null
			? t('chats.no_messages')
			: last.system !== null
				? t('chat.joined', { name: nameOf(last.system.user.displayName) })
				: last.mine
					? t('chats.you_prefix', { body: messageText(last) })
					: t('chats.sender_prefix', {
							name: nameOf(last.sender?.displayName ?? null),
							body: messageText(last),
						});

	return (
		<li {...popIn(index)}>
			<Link
				to={eventChatPath(chat.eventId)}
				className={`bg-surface rounded-card flex items-center gap-3 border p-3 ${
					chat.isNew ? 'border-primary shadow-primary/40 shadow-lg' : 'border-border'
				} ${pressable}`}
			>
				<CoverArt
					eventId={chat.eventId}
					category={chat.category}
					coverUrl={chat.coverUrl}
					className="rounded-input size-14 shrink-0"
				/>
				<span className="flex min-w-0 flex-1 flex-col gap-1">
					<span className="flex items-baseline gap-2">
						<span
							className={`text-body text-text min-w-0 flex-1 truncate ${unread ? 'font-bold' : 'font-semibold'}`}
						>
							{chat.title}
						</span>
						{last !== null && (
							<span
								className={`text-caption shrink-0 ${unread ? 'text-primary font-semibold' : 'text-text-muted'}`}
							>
								{listTime(new Date(last.createdAt), new Date(), tz, me.locale, t)}
							</span>
						)}
					</span>
					<span className="flex items-center gap-2">
						<span
							className={`text-caption min-w-0 flex-1 truncate ${unread ? 'text-text' : 'text-text-muted'}`}
						>
							{preview}
						</span>
						{unread && (
							<span
								aria-label={t('chats.unread', { count: chat.unread })}
								className="bg-primary text-text text-caption flex min-w-6 shrink-0 items-center justify-center rounded-full px-2 py-0.5 font-semibold"
							>
								{chat.unread > 99 ? '99+' : chat.unread}
							</span>
						)}
					</span>
					<span className="flex flex-wrap gap-1.5">
						{chat.isNew && (
							<span className={`${pill} bg-primary/20 text-primary font-semibold`}>
								{t('chats.new_chat')}
							</span>
						)}
						<span className={`${pill} bg-bg text-text-muted first-letter:uppercase`}>
							{formatDate(new Date(chat.startAt), tz, me.locale)}
						</span>
						{chat.closed && (
							<span className={`${pill} bg-bg text-text-muted`}>{t('chats.read_only')}</span>
						)}
					</span>
				</span>
			</Link>
		</li>
	);
}
